import { SanityParallaxHero, SanitySmallHero } from '../../types/generated/graphql-types'
import { MarketParallaxHeroProps } from '../components/blocks/market/MarketParallaxHero'
import { ParallaxHeroProps } from '../components/ParallaxHero'
import { SmallHeroProps } from '../components/SmallHero'
import { BlockIdMap } from '../utilities/blockId'
import { Breakpoints } from '../utilities/breakpoints'
import { validImage } from '../utilities/imageFilters'
import { Presets } from '../utilities/imagePreset'
import { getImagePropsWithSources } from '../utilities/imagePropUtils'
import { Translations } from '../utilities/localization'
import { GROUP_SITE_URL, MARKET_SITE_LINKS } from '../utilities/site'
import { mapSanityLinkToInternalExternalLink } from './LinkMapper'

export type MapSanityParallaxHeroToParallaxHeroPropsArgs = SanityParallaxHero & {
  parentTitle?: string
  useBusinessLinePreset?: boolean
}

export type MapSanityParallaxHeroToMarketParallaxHeroPropsArgs = SanityParallaxHero & {
  blockIdMap?: BlockIdMap
}

export type MapSanitySmallHeroToSmallHeroPropsArgs = SanitySmallHero & {
  themeAlias?: string
}

export const mapSanityParallaxHeroToParallaxHeroProps = ({
  blueOverlay,
  image,
  parentTitle,
  preambleText,
  preambleTitle,
  title,
  useBusinessLinePreset = false,
}: MapSanityParallaxHeroToParallaxHeroPropsArgs): ParallaxHeroProps => {
  const preset = useBusinessLinePreset
    ? Presets.parallaxHeroBusinessLine
    : Presets.parallaxHero
  const largeBreakpoint = useBusinessLinePreset ? Breakpoints.md : Breakpoints.lg

  return {
    blueOverlay: blueOverlay ?? undefined,
    backgroundImageProps: validImage(image)
      ? getImagePropsWithSources(
          image,
          {
            small: preset.small,
            medium: preset.medium,
            large: preset.large,
            extraLarge: preset.extraLarge,
          },
          Breakpoints.tb,
          largeBreakpoint,
          Breakpoints.xl
        )
      : undefined,
    detailsText: preambleText ?? undefined,
    detailsTitle: preambleTitle ?? undefined,
    title: title ?? '',
    parentTitle,
  }
}

export const mapSanityParallaxHeroToMarketParallaxHeroProps = (
  {
    _key,
    blockIdMap,
    blueOverlay,
    caption,
    image,
    imageLegalText,
    infoBoxButtonLabel,
    infoBoxHeading,
    link,
    preambleText,
    preambleTitle,
    title,
  }: MapSanityParallaxHeroToMarketParallaxHeroPropsArgs,
  translations: Translations
): MarketParallaxHeroProps => {
  const activeInfoBoxItem = MARKET_SITE_LINKS.find(
    marketLink => marketLink.url === process.env.GATSBY_BASE_URL
  )

  return {
    backgroundImageProps: validImage(image)
      ? getImagePropsWithSources(
          image,
          {
            small: Presets.marketParallaxHero.small,
            medium: Presets.marketParallaxHero.medium,
            large: Presets.marketParallaxHero.large,
            extraLarge: Presets.marketParallaxHero.extraLarge,
          },
          Breakpoints.tb,
          Breakpoints.sm,
          Breakpoints.lg
        )
      : undefined,
    imageLegalText: imageLegalText ?? undefined,
    blueOverlay: blueOverlay ?? undefined,
    caption: caption ?? undefined,
    infoBoxActiveItem: activeInfoBoxItem,
    infoBoxButtonLabel: infoBoxButtonLabel ?? undefined,
    infoBoxButtonUrl: GROUP_SITE_URL,
    infoBoxHeading: infoBoxHeading ?? undefined,
    infoBoxSelectItems: MARKET_SITE_LINKS.map(item => ({
      label: translations[item.label] ?? item.label,
      url: item.url,
    })),
    link: link ? mapSanityLinkToInternalExternalLink(link, blockIdMap) : undefined,
    parallaxId: _key ?? undefined,
    text: preambleText ?? undefined,
    theme: title ?? undefined,
    title: preambleTitle ?? undefined,
  }
}

export const mapSanitySmallHeroToSmallHeroProps = ({
  blueOverlay,
  image,
  theme,
  themeAlias,
  title,
}: MapSanitySmallHeroToSmallHeroPropsArgs): SmallHeroProps => ({
  blueOverlay: blueOverlay ?? undefined,
  image: validImage(image)
    ? getImagePropsWithSources(
        image,
        {
          small: Presets.smallHero.small,
          medium: Presets.smallHero.medium,
          large: Presets.smallHero.large,
          extraLarge: Presets.smallHero.extraLarge,
        },
        Breakpoints.tb,
        Breakpoints.md,
        Breakpoints.xxl
      )
    : undefined,
  theme: theme ?? themeAlias ?? '',
  title: title ?? '',
})
